import { useEffect, useMemo } from 'react';
import { communicationServices } from '@services';
import { getCleanedUrlParams, getUrl, showError } from '@utils';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from 'react-query';

import {
  selectCommunicationState,
  selectLoggedInUserState,
  changeSelectedEMailIds,
  changeCommunicationMailBoxId,
  changeCommunicationView,
} from '@store';
import { URLS } from 'layouts/DrawerLayout/constants';
import { useAssignees, useSelectorRef } from 'hooks/common';
import { SIDE_FILTERS_ENUM } from 'components/communication/CommunicationSideBar/constants';
import { FILTER_RULE_ENUMS } from '@constants';
import { QUERY_KEYS } from 'constants/queryKeys';
import {
  computeEmailListPayload,
  handleAssigneesMap,
  handleCommunicationData,
  handleMailBoxes,
} from './utils';
import { DEFAULT_MAILBOX } from './constants';

const POLLING_INTERVAL = 2 * 60 * 1000; // = 120000 = 2 minutes
export const COMMUNICATION_EMAIL_LIMIT = 200;

export const useCommunicationOperations = () => {
  const { pageCount, selectedEmailsIds, selectedMailboxId, isGroupView } =
    useSelector(selectCommunicationState);
  const currentUser = useSelectorRef(selectLoggedInUserState);
  const { assignees, isLoading: isAssigneesLoading } = useAssignees();

  const { push, isReady, query } = useRouter();
  const {
    orderBy = 'messageDate',
    order = 'desc',
    side = SIDE_FILTERS_ENUM.Inbox,
    mailboxId = selectedMailboxId,
    page = '1',
    assignee = undefined,
    teams = undefined,
    status = undefined,
    query: searchQuery = '',
    grouping = isGroupView,
  } = query;

  const dispatch = useDispatch();
  const mailBoxesParams = {
    orderBy: 'createdAt',
    order: 'desc',
    page: 1,
    pageSize: 1000,
  };
  const mailBoxesResponse = useQuery(
    [QUERY_KEYS.GET_MAILBOXES_KEY, mailBoxesParams],
    async () => {
      const payload = {
        type: FILTER_RULE_ENUMS.EmptyRuleType,
      };

      const response = await communicationServices.getEmailBoxes(
        mailBoxesParams,
        payload
      );

      // if no mailbox is selected, set the first one as default
      if (!selectedMailboxId) {
        const defaultMailbox = response?.data?.find(
          (mailbox) => mailbox?.address === DEFAULT_MAILBOX
        );
        dispatch(
          changeCommunicationMailBoxId({
            mailboxId: defaultMailbox?.id || response?.data?.[0]?.id,
          })
        );
      }
      return response;
    }
  );
  const mailBoxes = handleMailBoxes(mailBoxesResponse?.data?.data);

  const emailsResponse = useQuery(
    [
      QUERY_KEYS.GET_EMAILS_KEY,
      {
        page,
        orderBy,
        mailboxId,
        order,
        assignee,
        teams,
        status,
        side,
        searchQuery,
        grouping,
        pageSize: COMMUNICATION_EMAIL_LIMIT,
      },
    ],
    () => {
      const payloadParams = {
        assignee,
        team: teams,
        status,
        side,
        mailboxId,
      };
      const params = {
        page,
        orderBy,
        order,
        pageSize: COMMUNICATION_EMAIL_LIMIT,
        query: searchQuery,
      };
      const payload = computeEmailListPayload(
        getCleanedUrlParams(payloadParams)
      );
      dispatch(changeCommunicationMailBoxId({ mailboxId }));
      dispatch(changeCommunicationView({ grouping }));
      return communicationServices.getEmails({
        params: getCleanedUrlParams(params),
        payload,
      });
    },
    {
      refetchInterval: POLLING_INTERVAL,
      enabled: !!mailboxId && !!isReady && mailBoxesResponse.isFetched,
    }
  );

  const totalsResponse = useQuery(
    [QUERY_KEYS.GET_EMAIL_TOTALS_KEY, { mailboxId, grouping }],
    () => {
      return communicationServices.getTotals(mailboxId, grouping);
    },
    {
      refetchInterval: POLLING_INTERVAL,
      enabled: !!mailboxId && !!isReady && mailBoxesResponse.isFetched,
    }
  );

  const handleMarkAsReadUnread = async (emailIds, isRead) => {
    try {
      await communicationServices.editEmails({
        emailIds,
        action: { read: isRead },
      });
      emailsResponse.refetch();
    } catch (error) {
      showError(error);
    }
  };

  const handleMoveToTeam = async (emailIds, team) => {
    const isNone = team === 'None';
    const teamsFilters = teams ? teams.split(',') : [];

    try {
      await communicationServices.editEmails({
        emailIds,
        action: { team: isNone ? '' : team },
      });
      emailsResponse.refetch();

      if (teamsFilters.length && !teamsFilters.includes(team)) {
        dispatch(changeSelectedEMailIds([]));
      }
    } catch (error) {
      showError(error);
    }
  };

  const handleSetStatus = async (emailIds, statusValue) => {
    try {
      await communicationServices.editEmails({
        emailIds,
        action: { status: statusValue },
      });
      emailsResponse.refetch();
      if (status && statusValue !== status) {
        dispatch(changeSelectedEMailIds([]));
      }
    } catch (error) {
      showError(error);
    }
  };

  const handleSetPriority = async (emailIds, priorityValue) => {
    try {
      await communicationServices.editEmails({
        emailIds,
        action: { priority: priorityValue },
      });
      emailsResponse.refetch();
    } catch (error) {
      showError(error);
    }
  };

  const handleAssignPerson = async (emailIds, assigneeEmail = '') => {
    try {
      const assigneeFilters = assignee ? assignee.split(',') : [];
      await communicationServices.editEmails({
        action: { assigneeEmail: assigneeEmail || '' },
        emailIds,
      });
      await totalsResponse.refetch();

      emailsResponse.refetch();
      if (assigneeFilters.length && !assigneeFilters.includes(assigneeEmail)) {
        dispatch(changeSelectedEMailIds([]));
      }
    } catch (error) {
      showError(error);
    }
  };

  const handlePageChange = (newPage) => {
    const currentPage = query.page || '1';
    if (newPage !== currentPage) {
      push(getUrl({ ...query, page: newPage }, URLS.communication));
    }
  };

  const handleChangeSelectedEMailIds = (ids) => {
    dispatch(changeSelectedEMailIds(ids));
  };

  useEffect(() => {
    if (emailsResponse?.error) {
      showError(emailsResponse.error);
    }
  }, [emailsResponse?.error]);

  useEffect(() => {
    if (isReady && selectedMailboxId && !query.mailboxId) {
      push(
        getUrl({ ...query, mailboxId: selectedMailboxId }, URLS.communication)
      );
    }
  }, [isReady, selectedMailboxId, query.mailboxId, push]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    handleChangeSelectedEMailIds([]);
  }, [side, mailboxId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    handleChangeSelectedEMailIds([]);

    return () => {
      handleChangeSelectedEMailIds([]);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const totals = {
    ...(totalsResponse?.data?.data || {}),
    total: emailsResponse?.data?.meta?.total,
  };
  const assigneesMap = handleAssigneesMap(assignees);
  const communicationEmails = handleCommunicationData(
    emailsResponse?.data?.data || []
  );
  const agent = assignees?.find((item) => item.email === currentUser?.email);
  const selectedMailboxAddress = useMemo(() => {
    return mailBoxes?.find((email) => email.value === selectedMailboxId)?.text;
  }, [mailBoxes, selectedMailboxId]);

  return {
    side,
    agent,
    assignees,
    assigneesMap,
    communicationEmails,
    pageCount,
    page,
    totals,
    pageSize: COMMUNICATION_EMAIL_LIMIT,
    loading: emailsResponse?.isLoading || isAssigneesLoading,
    loaded: emailsResponse?.isSuccess,
    mailBoxes,
    currentUser,
    selectedMailboxId,
    selectedMailboxAddress,
    isGroupView,
    selectedEmailsIds,
    getEmails: emailsResponse.refetch,
    onMarkAsReadUnread: handleMarkAsReadUnread,
    nextPage: handlePageChange,
    onMoveToTeam: handleMoveToTeam,
    onSetStatus: handleSetStatus,
    onAssignPerson: handleAssignPerson,
    handleChangeSelectedEMailIds,
    handleSetPriority,
  };
};
